import React from 'react';

const ProcessSection = () => {
  const processSteps = [
    {
      step: '01',
      title: 'Discovery & Strategy',
      description: 'We analyze your business needs, data assets, and AI readiness to create a customized strategy and roadmap.',
      icon: 'bi-search',
      duration: '1-2 weeks',
      deliverables: ['AI Strategy Document', 'Technical Assessment', 'ROI Projection']
    },
    {
      step: '02',
      title: 'Design & Architecture',
      description: 'Our team designs the AI solution architecture and creates detailed technical specifications and plans.',
      icon: 'bi-diagram-3',
      duration: '1-2 weeks',
      deliverables: ['Solution Architecture', 'Technical Specifications', 'Implementation Plan']
    },
    {
      step: '03',
      title: 'Development & Integration',
      description: 'We build and integrate your AI solution with your existing systems and workflows for seamless operation.',
      icon: 'bi-code-slash',
      duration: '2-4 weeks',
      deliverables: ['AI Solution', 'System Integration', 'Testing & Validation']
    },
    {
      step: '04',
      title: 'Deployment & Support',
      description: 'We deploy your solution and provide ongoing support to ensure optimal performance and success.',
      icon: 'bi-rocket-takeoff',
      duration: '1 week',
      deliverables: ['Production Deployment', 'Training & Documentation', 'Ongoing Support']
    }
  ];

  return (
    <section className="process-section py-5" style={{
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      position: 'relative',
      overflow: 'hidden',
      borderTop: '1px solid rgba(14, 165, 233, 0.1)',
      borderBottom: '1px solid rgba(14, 165, 233, 0.1)'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.03) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.02) 0%, transparent 50%)
        `,
        zIndex: 1
      }}></div>

      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <div className="row justify-content-center text-center mb-5">
          <div className="col-lg-8">
            <h2 style={{
              fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
              fontWeight: '700',
              color: '#1e293b',
              marginBottom: '1rem',
              lineHeight: '1.2'
            }}>
              Proven{' '}
              <span style={{
                background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Methodology</span>
            </h2>
            <p style={{
              fontSize: '1.1rem',
              color: '#64748b',
              lineHeight: '1.6',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Our systematic approach ensures successful AI implementation with measurable results
            </p>
          </div>
        </div>

        <div className="row g-3 g-md-4">
          {processSteps.map((step, index) => (
            <div key={index} className="col-12 col-sm-6 col-lg-3">
              <div style={{
                background: '#ffffff',
                border: '2px solid #94a3b8',
                borderRadius: 'clamp(16px, 3vw, 20px)',
                padding: 'clamp(1.5rem, 4vw, 2.5rem)',
                height: '100%',
                minHeight: 'clamp(450px, 70vw, 600px)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 15px 35px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.borderColor = '#94a3b8';
              }}>
                {/* Step Number */}
                <div style={{
                  position: 'absolute',
                  top: 'clamp(1rem, 3vw, 1.5rem)',
                  right: 'clamp(1rem, 3vw, 1.5rem)',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
                  color: 'white',
                  width: 'clamp(28px, 6vw, 32px)',
                  height: 'clamp(28px, 6vw, 32px)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(0.7rem, 1.8vw, 0.8rem)',
                  fontWeight: '700',
                  boxShadow: '0 3px 12px rgba(14, 165, 233, 0.3)'
                }}>
                  {step.step}
                </div>

                {/* Responsive Grid Layout */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'clamp(0.75rem, 2vw, 1rem)',
                  height: '100%'
                }}>
                  {/* Row 1: Icon + Title */}
                  <div style={{
                    minHeight: 'clamp(50px, 10vw, 60px)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'clamp(0.75rem, 2vw, 1rem)'
                  }}>
                    <div style={{
                      width: 'clamp(35px, 7vw, 45px)',
                      height: 'clamp(35px, 7vw, 45px)',
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
                      borderRadius: 'clamp(8px, 2vw, 12px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 6px 15px rgba(14, 165, 233, 0.3)',
                      flexShrink: 0
                    }}>
                      <i className={`bi ${step.icon}`} style={{
                        color: 'white',
                        fontSize: 'clamp(1rem, 2.5vw, 1.2rem)'
                      }}></i>
                    </div>
                    <h3 style={{
                      fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                      fontWeight: '700',
                      color: '#1e293b',
                      lineHeight: '1.3',
                      margin: 0
                    }}>
                      {step.title}
                    </h3>
                  </div>

                  {/* Row 2: Descriptions */}
                  <div style={{
                    minHeight: 'clamp(80px, 15vw, 100px)',
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}>
                    <p style={{
                      fontSize: 'clamp(0.85rem, 2.2vw, 0.95rem)',
                      color: '#64748b',
                      lineHeight: '1.6',
                      margin: 0
                    }}>
                      {step.description}
                    </p>
                  </div>

                  {/* Row 3: Duration Boxes */}
                  <div style={{
                    minHeight: 'clamp(50px, 10vw, 60px)',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      background: 'rgba(14, 165, 233, 0.1)',
                      borderRadius: 'clamp(8px, 2vw, 12px)',
                      padding: 'clamp(0.6rem, 1.5vw, 0.75rem)',
                      border: '2px solid rgba(14, 165, 233, 0.5)',
                      width: '100%'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'clamp(0.375rem, 1vw, 0.5rem)'
                      }}>
                        <i className="bi bi-clock" style={{
                          color: '#0ea5e9',
                          fontSize: 'clamp(0.8rem, 2vw, 1rem)'
                        }}></i>
                        <span style={{
                          fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
                          fontWeight: '600',
                          color: '#1e293b'
                        }}>
                          Duration: {step.duration}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Row 4: Deliverables Heading */}
                  <div style={{
                    minHeight: 'clamp(30px, 6vw, 35px)',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <h4 style={{
                      fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                      fontWeight: '600',
                      color: '#0ea5e9',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      margin: 0
                    }}>
                      Key Deliverables
                    </h4>
                  </div>

                  {/* Row 5: Deliverables List */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'clamp(0.375rem, 1vw, 0.5rem)'
                  }}>
                    {step.deliverables.map((deliverable, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'clamp(0.375rem, 1vw, 0.5rem)',
                        fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                        color: '#64748b',
                        minHeight: 'clamp(20px, 4vw, 25px)',
                        lineHeight: '1.3'
                      }}>
                        <i className="bi bi-check-circle-fill" style={{
                          color: '#10b981',
                          fontSize: 'clamp(0.6rem, 1.5vw, 0.7rem)',
                          flexShrink: 0
                        }}></i>
                        {deliverable}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Process Summary */}
        <div className="row justify-content-center mt-5">
          <div className="col-lg-8">
            <div style={{
              background: 'rgba(14, 165, 233, 0.05)',
              border: '2px solid rgba(14, 165, 233, 0.4)',
              borderRadius: '20px',
              padding: '2.5rem',
              textAlign: 'center'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#1e293b',
                marginBottom: '1rem'
              }}>
                Total Timeline: 8-12 Weeks
              </h3>
              <p style={{
                fontSize: '1rem',
                color: '#64748b',
                lineHeight: '1.6',
                marginBottom: '1.5rem'
              }}>
                From initial discovery to production deployment, we deliver your AI solution in just 8-12 weeks with our proven methodology.
              </p>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '2rem',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#64748b',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  <i className="bi bi-shield-check" style={{ color: '#10b981' }}></i>
                  <span>98% Success Rate</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#64748b',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  <i className="bi bi-lightning" style={{ color: '#f59e0b' }}></i>
                  <span>Fast Delivery</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#64748b',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  <i className="bi bi-award" style={{ color: '#0ea5e9' }}></i>
                  <span>Proven Results</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
